import {getAuth} from 'firebase/auth';

import {ObsProcessingRow, SiteInfoRow} from '../setupWizardConstants';
import {API_ROOT, SLOW_API_ROOT, makeApiRequest} from './api';

export interface SetupWizardOrganization {
  id: string;
  name: string;
}

export interface SetupWizardProject {
  id: string;
  displayName: string;
}

export interface UploadedObsSiteSummary {
  siteName: string;
  hasData: boolean;
  startDate?: string | null;
  endDate?: string | null;
  rowCount?: number | null;
}

/** A row from the output delineation DataFrame. Known fields listed; extras possible from backend. */
export interface OutputDelineationRow {
  name?: string;
  outlet_name?: string;
  is_outlet?: boolean;
  lon?: number;
  lat?: number;
  gauge_id?: string;
  gauge_type?: string;
  flow_column_name?: string;
  distributed_flow_type?: string;
  local_timezone?: string;
  output_units?: string;
  paid_for?: boolean | null;
  [key: string]: unknown;
}

/** A public gauge entry (subset of output delineation for non-private gauge sources). */
export interface PublicGauge {
  name?: string;
  lat?: number;
  lng?: number;
  lon?: number;
  gauge_id?: string;
  gauge_type?: string;
  flow_column_name?: string;
  distributed_flow_type?: string;
  [key: string]: unknown;
}

/** Per-site cleanup parameters (mask thresholds and smoothing). */
export interface SiteCleanupParams {
  mask_above?: number | null;
  mask_below?: number | null;
  discharge_gaussian_smooth_sigma?: number | null;
}

/**
 * API response shape for setup wizard state.
 * Uses camelCase to match the API response (generated from Pydantic with alias_generator=to_camel).
 */
export interface SiteShapefileOverride {
  siteName: string;
  downstreamSite: string;
  hasFile: boolean;
}

export interface SetupWizardStateView {
  organizationId: string | null;
  project: string | null;
  organizationName: string | null;
  projectReadable: string | null;
  units: string;
  timeZone: string | null;
  skipGaugeFinder: boolean;
  maxSubbasinArea: number;
  delineationType: string;
  overrideFromShapefile: boolean;
  siteInputValues: Record<string, SiteCleanupParams>;
  siteShapefileOverrides: SiteShapefileOverride[];
  siteInfo: SiteInfoRow[];
  obsProcessing: ObsProcessingRow[];
  outputDelineation: OutputDelineationRow[] | null;
  uploadedObsSummary: UploadedObsSiteSummary[];
  hasComparisonBasin: boolean;
  hasDelineation: boolean;
  delineationGeojson: {
    basins: GeoJSON.FeatureCollection;
    rivers: GeoJSON.FeatureCollection | null;
  } | null;
  forecastPoints: string[];
  contributingBasinsByForecastPoint?: Record<string, string[]>;
  publicGauges: PublicGauge[] | null;
  newSiteListId: string | null;
  committedFcid: number | null;
  commitOutput: string | null;
  gaugeCommitOutput: string | null;
  gaugeCommittedSites: string[];
}

export interface CategorizedValidationErrors {
  sites: string[];
  observations: string[];
}

export interface SetupWizardMutationPayload {
  data: SetupWizardStateView;
  validationErrors: CategorizedValidationErrors;
  validationWarnings: string[];
  cliCommand?: string;
}

export interface SiteObservationsPayload {
  obs: Array<Record<string, number | null | string>> | null;
  precipitation: Array<Record<string, number | null | string>> | null;
  temperature: Array<Record<string, number | null | string>> | null;
}

export interface FetchPublicObsPayload extends SetupWizardMutationPayload {
  fetchedSites: string[];
  fetchErrors: string[];
}

/** Result from the inspect-obs endpoint: column names, sheet list, and a head preview. */
export interface ObsInspectResult {
  columns: string[];
  sheets: string[] | null;
  head: Record<string, string>[];
}

/** Per-upload config passed alongside the file to the upload-observations endpoint. */
export interface SiteObsUploadConfig {
  observation_column_name: string;
  datetime_column_name: string;
  timezone_of_observations: string;
  data_frequency: string;
  data_units: string;
  sample_timing: string;
  type_of_flow: string;
  data_source_type: string;
  missing_data_value: string;
  sheet_name?: string;
  header?: number;
  dayfirst?: boolean;
}

/**
 * Parse error response from setup wizard mutation endpoints (may include traceback).
 *
 * Used by endpoints that hit SLOW_API_ROOT and return JSON errors with {error, traceback}.
 * Blob download endpoints (geopackage, setup-csv) use simple `throw new Error(text)` instead
 * since they don't return JSON on error.
 */
async function throwMutationError(res: Response, fallback: string): Promise<never> {
  const text = await res.text();
  let msg = text || fallback;
  try {
    const parsed = JSON.parse(text);
    const inner = parsed?.data ?? parsed;
    const tb = inner?.traceback ?? '';
    msg = inner?.error ?? msg;
    if (tb) msg = `${msg}\n\n${tb}`;
  } catch {
    // text wasn't JSON, use as-is
  }
  throw new Error(msg);
}

/** PATCH body for updating scalar project settings. */
export type UpdateSettingsBody = Partial<
  Pick<
    SetupWizardStateView,
    | 'units'
    | 'timeZone'
    | 'skipGaugeFinder'
    | 'maxSubbasinArea'
    | 'delineationType'
    | 'overrideFromShapefile'
  >
>;

export type SetupProjectQueryKey = readonly ['setupWizard', 'project', string, string];

export function makeSetupProjectsQueryKey(orgId: string | null) {
  return ['setupWizard', 'projects', orgId] as const;
}

export interface SetupSectionProps {
  orgId: string;
  projectId: string;
  payload: SetupWizardMutationPayload;
  queryKey: SetupProjectQueryKey;
  stepErrors: string[];
  prerequisiteWarning: string | null;
}

// Re-export row types from constants for convenience
export type {ObsProcessingRow, SiteInfoRow} from '../setupWizardConstants';

/** Return type for getStateViewDefaults with proper typing */
export interface StateViewDefaults {
  siteInfo: SiteInfoRow[];
  obsProcessing: ObsProcessingRow[];
  units: string;
  timeZone: string;
  delineationType: string;
  maxSubbasinArea: number;
  skipGaugeFinder: boolean;
  outputDelineation: OutputDelineationRow[];
  publicGauges: PublicGauge[];
  forecastPoints: string[];
  contributingBasinsByForecastPoint: Record<string, string[]> | undefined;
  uploadedObsSummary: UploadedObsSiteSummary[];
  siteInputValues: Record<string, SiteCleanupParams>;
  siteShapefileOverrides: SiteShapefileOverride[];
  hasComparisonBasin: boolean;
  hasDelineation: boolean;
  delineationGeojson: SetupWizardStateView['delineationGeojson'];
}

/**
 * Helper to get default values for optional state view fields.
 * Use this when payload.data might be undefined during loading.
 */
export function getStateViewDefaults(data: SetupWizardStateView | undefined): StateViewDefaults {
  return {
    siteInfo: data?.siteInfo ?? [],
    obsProcessing: data?.obsProcessing ?? [],
    units: data?.units ?? 'US',
    timeZone: data?.timeZone ?? '',
    delineationType: data?.delineationType ?? 'Basic',
    maxSubbasinArea: data?.maxSubbasinArea ?? 500,
    skipGaugeFinder: data?.skipGaugeFinder ?? false,
    outputDelineation: data?.outputDelineation ?? [],
    publicGauges: data?.publicGauges ?? [],
    forecastPoints: data?.forecastPoints ?? [],
    contributingBasinsByForecastPoint: data?.contributingBasinsByForecastPoint,
    uploadedObsSummary: data?.uploadedObsSummary ?? [],
    siteInputValues: data?.siteInputValues ?? {},
    siteShapefileOverrides: data?.siteShapefileOverrides ?? [],
    hasComparisonBasin: data?.hasComparisonBasin ?? false,
    hasDelineation: data?.hasDelineation ?? false,
    delineationGeojson: data?.delineationGeojson ?? null,
  };
}

function throwOnApiError<T>(res: {data?: T; error?: string}): T {
  if (!res.data) {
    let msg = 'Request failed';
    let tb = '';
    try {
      const parsed = JSON.parse(res.error ?? '');
      msg = parsed.error ?? res.error ?? msg;
      tb = parsed.traceback ?? '';
    } catch {
      msg = res.error ?? msg;
    }
    throw new Error(tb ? `${msg}\n\n${tb}` : msg);
  }
  return res.data;
}

function basePath(orgId: string): string {
  return `setup_wizard/organizations/${encodeURIComponent(orgId)}`;
}

function projectPath(orgId: string, projectId: string): string {
  // Backend stores project IDs with underscores (see routes.py _normalize_project_id).
  // Replace spaces before encoding so the URL path matches the canonical stored ID.
  const normalized = projectId.replace(/ /g, '_');
  return `${basePath(orgId)}/projects/${encodeURIComponent(normalized)}`;
}

async function authedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const auth = getAuth();
  const headers = new Headers(options.headers);
  if (auth.currentUser) {
    const token = await auth.currentUser.getIdToken();
    headers.set('Authorization', `jwt ${token}`);
  }
  return fetch(url, {...options, headers});
}

export const setupWizardApi = {
  listOrganizations: async (): Promise<SetupWizardOrganization[] | undefined> =>
    (
      await makeApiRequest<SetupWizardOrganization[]>(
        'setup_wizard/organizations',
        null,
        {},
        {noContextOrg: true}
      )
    ).data,

  listProjects: async (orgId: string): Promise<SetupWizardProject[] | undefined> =>
    (
      await makeApiRequest<SetupWizardProject[]>(
        `${basePath(orgId)}/projects`,
        null,
        {},
        {noContextOrg: true}
      )
    ).data,

  createProject: async (
    orgId: string,
    projectName: string
  ): Promise<SetupWizardMutationPayload> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${basePath(orgId)}/projects`,
      null,
      {
        method: 'POST',
        body: JSON.stringify({projectName: projectName.trim()}),
      },
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  renameProject: async (
    orgId: string,
    projectId: string,
    displayName: string
  ): Promise<void> => {
    const res = await makeApiRequest<void>(
      `${projectPath(orgId, projectId)}/rename`,
      null,
      {
        method: 'PATCH',
        body: JSON.stringify({displayName}),
      },
      {noContextOrg: true}
    );
    throwOnApiError(res);
  },

  duplicateProject: async (
    orgId: string,
    projectId: string,
    newDisplayName: string
  ): Promise<SetupWizardMutationPayload> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/duplicate`,
      null,
      {
        method: 'POST',
        body: JSON.stringify({newDisplayName}),
      },
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  getProject: async (
    orgId: string,
    projectId: string
  ): Promise<SetupWizardMutationPayload | undefined> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      projectPath(orgId, projectId),
      null,
      {},
      {noContextOrg: true}
    );
    return res.data;
  },

  updateSettings: async (
    orgId: string,
    projectId: string,
    body: UpdateSettingsBody
  ): Promise<SetupWizardMutationPayload> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/settings`,
      null,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  updateSiteInfo: async (
    orgId: string,
    projectId: string,
    rows: SiteInfoRow[]
  ): Promise<SetupWizardMutationPayload> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/site-info`,
      null,
      {
        method: 'PUT',
        body: JSON.stringify(rows),
      },
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  updateObsConfig: async (
    orgId: string,
    projectId: string,
    rows: ObsProcessingRow[]
  ): Promise<SetupWizardMutationPayload> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/obs-config`,
      null,
      {
        method: 'PUT',
        body: JSON.stringify(rows),
      },
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  updateCleanupParams: async (
    orgId: string,
    projectId: string,
    siteInputValues: Record<string, SiteCleanupParams>
  ): Promise<SetupWizardMutationPayload> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/cleanup-params`,
      null,
      {
        method: 'PATCH',
        body: JSON.stringify(siteInputValues),
      },
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  applyDelineation: async (
    orgId: string,
    projectId: string
  ): Promise<SetupWizardMutationPayload> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/apply-delineation`,
      null,
      {method: 'POST'},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  deleteProject: async (
    orgId: string,
    projectId: string,
    confirmProjectName: string
  ): Promise<void> => {
    await makeApiRequest<void>(
      projectPath(orgId, projectId),
      null,
      {
        method: 'DELETE',
        body: JSON.stringify({confirmProjectName}),
      },
      {noContextOrg: true}
    );
  },

  uploadSiteShapefile: async (
    orgId: string,
    projectId: string,
    siteName: string,
    downstreamSite: string,
    file: File
  ): Promise<SetupWizardMutationPayload> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('site_name', siteName);
    formData.append('downstream_site', downstreamSite);
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/site-shapefile`,
      null,
      {method: 'POST', body: formData},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  deleteSiteShapefile: async (
    orgId: string,
    projectId: string,
    siteName: string
  ): Promise<SetupWizardMutationPayload> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/site-shapefile/${encodeURIComponent(siteName)}`,
      null,
      {method: 'DELETE'},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  uploadCustomShapefile: async (
    orgId: string,
    projectId: string,
    file: File
  ): Promise<SetupWizardMutationPayload> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/custom-shapefile`,
      null,
      {method: 'POST', body: formData},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  deleteCustomShapefile: async (
    orgId: string,
    projectId: string
  ): Promise<SetupWizardMutationPayload> => {
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/custom-shapefile`,
      null,
      {method: 'DELETE'},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  /** Uses SLOW_API_ROOT for longer request timeout. */
  runDelineation: async (orgId: string, projectId: string): Promise<SetupWizardMutationPayload> => {
    const url = `${SLOW_API_ROOT}/${projectPath(orgId, projectId)}/delineation`;
    const res = await authedFetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
    });
    if (!res.ok) await throwMutationError(res, 'Delineation failed');
    const json = await res.json();
    return json.data as SetupWizardMutationPayload;
  },

  /** POST a file to the inspect-obs endpoint to retrieve column names, sheets, and a head preview. */
  inspectObsFile: async (
    orgId: string,
    projectId: string,
    file: File,
    options?: {sheetName?: string; header?: number}
  ): Promise<ObsInspectResult> => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.sheetName != null) formData.append('sheet_name', options.sheetName);
    if (options?.header != null) formData.append('header', String(options.header));
    const res = await makeApiRequest<ObsInspectResult>(
      `${projectPath(orgId, projectId)}/inspect-obs`,
      null,
      {method: 'POST', body: formData},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  uploadObservations: async (
    orgId: string,
    projectId: string,
    siteName: string,
    file: File,
    config?: SiteObsUploadConfig
  ): Promise<SetupWizardMutationPayload> => {
    const formData = new FormData();
    formData.append('file', file);
    if (config) {
      for (const [key, val] of Object.entries(config)) {
        if (val != null) formData.append(key, String(val));
      }
    }
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/sites/${encodeURIComponent(siteName)}/observations`,
      null,
      {method: 'POST', body: formData},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  bulkUploadObservations: async (
    orgId: string,
    projectId: string,
    files: Record<string, File>
  ): Promise<SetupWizardMutationPayload> => {
    const formData = new FormData();
    for (const [siteName, file] of Object.entries(files)) {
      formData.append(siteName, file);
    }
    const res = await makeApiRequest<SetupWizardMutationPayload>(
      `${projectPath(orgId, projectId)}/observations/bulk`,
      null,
      {method: 'POST', body: formData},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  previewObservations: async (
    orgId: string,
    projectId: string,
    siteName: string,
    file: File,
    config: SiteObsUploadConfig
  ): Promise<SiteObservationsPayload> => {
    const formData = new FormData();
    formData.append('file', file);
    for (const [key, val] of Object.entries(config)) {
      if (val != null) formData.append(key, String(val));
    }
    const res = await makeApiRequest<SiteObservationsPayload>(
      `${projectPath(orgId, projectId)}/sites/${encodeURIComponent(siteName)}/obs-preview`,
      null,
      {method: 'POST', body: formData},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  getSiteObservations: async (
    orgId: string,
    projectId: string,
    siteName: string
  ): Promise<SiteObservationsPayload | undefined> => {
    const res = await makeApiRequest<SiteObservationsPayload>(
      `${projectPath(orgId, projectId)}/sites/${encodeURIComponent(siteName)}/observations`,
      null,
      {},
      {noContextOrg: true}
    );
    return res.data;
  },

  fetchPublicObservations: async (
    orgId: string,
    projectId: string
  ): Promise<FetchPublicObsPayload> => {
    const res = await makeApiRequest<FetchPublicObsPayload>(
      `${projectPath(orgId, projectId)}/observations/fetch-public`,
      null,
      {method: 'POST'},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  /** Direct blob download from API. */
  downloadGeopackage: async (orgId: string, projectId: string): Promise<Blob> => {
    const url = `${API_ROOT}/${projectPath(orgId, projectId)}/artifacts/geopackage`;
    const res = await authedFetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.blob();
  },

  parseSiteCsv: async (orgId: string, projectId: string, file: File): Promise<SiteInfoRow[]> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await makeApiRequest<SiteInfoRow[]>(
      `${projectPath(orgId, projectId)}/artifacts/parse-site-csv`,
      null,
      {method: 'POST', body: formData},
      {noContextOrg: true}
    );
    return throwOnApiError(res);
  },

  /** Direct blob download of the complete setup CSV from the API. */
  downloadSetupCsv: async (orgId: string, projectId: string): Promise<Blob> => {
    const url = `${API_ROOT}/${projectPath(orgId, projectId)}/artifacts/setup-csv`;
    const res = await authedFetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.blob();
  },

  /** Uses SLOW_API_ROOT — commits delineation (FeatureCollection + SiteList) to DB. */
  commitDelineation: async (
    orgId: string,
    projectId: string
  ): Promise<SetupWizardMutationPayload> => {
    const url = `${SLOW_API_ROOT}/${projectPath(orgId, projectId)}/commit-delineation`;
    const res = await authedFetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
    });
    if (!res.ok) await throwMutationError(res, 'Commit delineation failed');
    const json = await res.json();
    return json.data as SetupWizardMutationPayload;
  },

  /** Uses SLOW_API_ROOT — commits gauge data for selected sites to DB. */
  commitGaugeData: async (
    orgId: string,
    projectId: string,
    body: {siteNames: string[]}
  ): Promise<SetupWizardMutationPayload> => {
    const url = `${SLOW_API_ROOT}/${projectPath(orgId, projectId)}/commit-gauge-data`;
    const res = await authedFetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(body),
    });
    if (!res.ok) await throwMutationError(res, 'Commit gauge data failed');
    const json = await res.json();
    return json.data as SetupWizardMutationPayload;
  },
};
